import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useContext,
} from 'react';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';
import {
  DEAL_NOTES_OPEN_VALUE,
  LEAD_BOARD_QUERY_PARAM,
} from '../../subrouters/Seller/models';
import { Listing } from 'models/Listing';
import usePortfolioLinks from './hooks/usePortfolioLinks';
import InviteListingViewer from 'components/InviteListingViewer';
import useCallbackRef from 'hooks/useCallbackRef';
import { DealRoom } from 'models/DealRoom';
import Subnav from 'components/Subnav';
import useMatchMedia from 'hooks/useMatchMedia';
import { MoreDropdown } from '../MoreDropdown';
import { ViewSelect } from '../ViewSelect';
import { NEW_WINDOW_PROPS } from 'data/global';
import useDropdownOptions from './hooks/useDropdownOptions';
import OverflowMenu from '../OverflowMenu/OverflowMenu.component';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { DealNotes } from '../DealNotes';
import { SubnavLinks } from 'components/Subnav/Subnav.component';
import utils, {
  checkHasListing,
  checkIsDraft,
  checkIsPrivate,
  checkIsPublic,
  checkIsPublished,
  getCreateListingRoute,
  getHasElementsInArray,
  getPrivateListingRoute,
  getResolveUrl,
  hasNotSeenDiscoveryFeature,
} from 'utils';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import NotesIcon from 'icons/Notes.icon';
import PlusIcon from 'icons/Plus.icon';
import SendIcon from 'icons/Send.icon';
import ResolvePopup from 'components/ResolvePopup';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import SpeakerphoneIcon from 'icons/Speakerphone.icon';
import Counter from 'components/Counter';
import { SIZE } from 'models/Size';
import { COUNTER_VARIANTS } from 'components/Counter/Counter.component';
import useBroadcast from './hooks/useBroadcast';
import AddLeadModal from 'components/AddLeadModal';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import {
  INACTIVE_PAGE,
  ONGOING_PAGE,
} from '../../subrouters/Seller/subrouters/ListingDetailSeller/providers/Leads/Leads.provider';
import ShareListingModal from 'components/ShareListingModal';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import ShareListingWithPreviousLeads from 'components/ShareListingWithPreviousLeads';
import { FIFTH_RELEASE_DISCOVERING_FEATURE } from 'data/discoveringFeature';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './PortfolioSubnav.styles.scss';

export type PortfolioAs = 'seller' | 'buyer';

type PortfolioSubnavProps = {
  className?: string;
  as: PortfolioAs;
  listing: Partial<Listing>;
  dealRoom: Partial<DealRoom>;
  canManageDeal?: boolean;
  sessionId?: string;
  isTableView?: boolean;
  hasUnlockPremiumFeatures?: boolean; // for seller only
  unreadMessagesCount?: number;
  canAccessDataRoom: boolean;
  openDealStatusDrawer?: () => void;
  openManageListView?: () => void;
  updateDealRoom?: (dealRoom: Partial<DealRoom>) => void;
};

const PortfolioSubnav: React.FC<PortfolioSubnavProps> = (props) => {
  const {
    className,
    as,
    listing,
    dealRoom,
    sessionId,
    isTableView,
    unreadMessagesCount,
    canAccessDataRoom,
    hasUnlockPremiumFeatures = false,
    canManageDeal = true,
    openDealStatusDrawer,
    openManageListView,
    updateDealRoom,
  } = props;

  const {
    dataRoomId,
    industry,
    slugName,
    type,
    _id: listingId,
    listingTitle,
  } = listing || {};

  const [inviteListingViewerModal, inviteListingViewerModalRef] =
    useCallbackRef<ModalRef>(null);
  const [makeNewLeadModal, makeNewLeadModalRef] = useCallbackRef(null);
  const [shareListingModal, shareListingModalRef] = useCallbackRef<ModalRef>();
  const [shareWithPreviousLeadsModal, shareWithPreviousLeadsModalRef] =
    useCallbackRef<ModalRef>();
  const [dealNotesDrawer, dealNotesDrawerRef] =
    useCallbackRef<SideDrawerRef>(null);

  const { limit, updateLimit } = useNumberOfResultsPerPage({
    pageParam: [ONGOING_PAGE, INACTIVE_PAGE],
  });

  const {
    currentUser,
    currentUser: { seenShareListingFeature },
  } = useContext(CurrentUserContext);

  const hasListing = checkHasListing(dealRoom?.listingStage);
  const isPrivate = checkIsPrivate(dealRoom?.listingStage);
  const isPublic = checkIsPublic(dealRoom?.listingStage);
  const isPublished = checkIsPublished(dealRoom?.listingStage);
  const isDraft = checkIsDraft(dealRoom?.listingStage);

  const { pathname } = useLocation();
  const { t } = useTranslation();

  const isAboveLaptop = useMatchMedia('isAboveLaptop');

  const isDisabled = !canManageDeal;

  const businessLink = utils.composeBusinessUrl(industry, slugName, type);

  const updateListingLink = getCreateListingRoute(
    dealRoom?._id,
    listingId,
    type,
    isPublished,
  );

  const links = usePortfolioLinks({
    as,
    dataRoomId,
    hasListing,
    sessionId,
    unreadMessagesCount,
    canAccessDataRoom,
    canSeeAllTabs: canManageDeal,
  });

  const onDealNote = useCallback(() => {
    dealNotesDrawer?.setOpen(true);
    updateDealRoom?.({ unreadNotesCount: 0 });
  }, [dealNotesDrawer, updateDealRoom]);

  // Deep-link support: open the deal-notes drawer when navigated to with the
  // `dealNotes=open` query param (e.g. from an unread deal notes suggested
  // action). Runs once, then clears the param.
  const {
    params: { dealNotes },
    removeQueryParam,
  } = useQueryParams<{ dealNotes: string }>();

  const hasOpenedDealNotesRef = useRef(false);

  useEffect(() => {
    if (
      dealNotes !== DEAL_NOTES_OPEN_VALUE ||
      !dealNotesDrawer ||
      hasOpenedDealNotesRef.current
    )
      return;

    hasOpenedDealNotesRef.current = true;
    onDealNote();
    removeQueryParam(LEAD_BOARD_QUERY_PARAM.DEAL_NOTES);
  }, [dealNotes, dealNotesDrawer, onDealNote, removeQueryParam]);

  const onMakeNewLead = useCallback(
    () => makeNewLeadModal?.open(),
    [makeNewLeadModal],
  );

  const onInvitePrivateListing = useCallback(
    () => inviteListingViewerModal?.open(),
    [inviteListingViewerModal],
  );

  const copyToClipboard = useCopyToClipboard();

  const copyPrivateLink = useCallback(() => {
    if (isDisabled) return;

    copyToClipboard(null, getResolveUrl(getPrivateListingRoute(listingId)));
  }, [isDisabled, listingId, copyToClipboard]);

  const {
    canSendBroadcastPulseCheck,
    canSendBroadcastMessage,
    RenderedBroadcastModal,
    openBroadcastModal,
  } = useBroadcast();

  const { showButtons, dropdownOptions } = useDropdownOptions({
    as,
    isPrivate,
    hasUnlockPremiumFeatures,
    isDisabled,
    isDraft,
    updateListingLink,
    hasListing,
    canSendBroadcastPulseCheck,
    canSendBroadcastMessage,
    onInvitePrivateListing,
    onMakeNewLead,
    onDealNote,
    openDealStatusDrawer,
    copyPrivateLink,
    openManageListView,
    openBroadcastModal,
  });

  const onInvitePrivate = useCallback(() => {
    makeNewLeadModal?.close();
    inviteListingViewerModal?.open();
  }, [inviteListingViewerModal, makeNewLeadModal]);

  const onAddLead = useCallback(() => {
    inviteListingViewerModal?.close();
    makeNewLeadModal?.open();
  }, [inviteListingViewerModal, makeNewLeadModal]);

  const isBuyer = useMemo(() => as === 'buyer', [as]);

  const showSellerButtons = useMemo(
    () => !isBuyer && showButtons,
    [isBuyer, showButtons],
  );

  const isOnLeadsTab = useMemo(() => pathname.includes('leads'), [pathname]);

  const showLimitFilter = useMemo(
    () => isAboveLaptop && isTableView && isOnLeadsTab,
    [isOnLeadsTab, isAboveLaptop, isTableView],
  );

  const RenderLeadsToggle = useMemo(() => {
    if (!showSellerButtons || !isOnLeadsTab || !isAboveLaptop) return null;

    return (
      <ViewSelect updateProp="isGridViewForLeads" isTableView={isTableView} />
    );
  }, [showSellerButtons, isOnLeadsTab, isAboveLaptop, isTableView]);

  const NewLeadModal = useMemo(() => {
    if (!showSellerButtons) return null;

    return (
      <AddLeadModal
        ref={makeNewLeadModalRef}
        dealRoom={dealRoom}
        onInvitePrivate={onInvitePrivate}
      />
    );
  }, [showSellerButtons, dealRoom, makeNewLeadModalRef, onInvitePrivate]);

  const InviteViewerModal = useMemo(() => {
    if (!showSellerButtons) return null;

    return (
      <InviteListingViewer
        ref={inviteListingViewerModalRef}
        listingId={listingId}
        isPrivate={isPrivate}
        onAddLead={onAddLead}
      />
    );
  }, [
    isPrivate,
    showSellerButtons,
    listingId,
    inviteListingViewerModalRef,
    onAddLead,
  ]);

  const classes = classNames(
    'portfolio-subnav',
    `portfolio-subnav--${as}`,
    className,
  );

  const subnavTitle = useMemo(
    () => links.find(({ to }) => pathname.includes(to))?.label,
    [links, pathname],
  );

  const subnavOptions = useMemo(
    () => links.filter(({ to }) => !pathname.includes(to)),
    [links, pathname],
  );

  return (
    <div className={classes}>
      {!isAboveLaptop ? (
        <OverflowMenu title={subnavTitle}>
          <ul>
            {subnavOptions.map(
              ({ to, label, openInNewTab, count = 0 }: SubnavLinks) => (
                <li key={to || label}>
                  <Link to={to} {...(openInNewTab ? NEW_WINDOW_PROPS : {})}>
                    {label}

                    <Counter count={count} />
                  </Link>
                </li>
              ),
            )}
          </ul>
        </OverflowMenu>
      ) : (
        <Subnav links={links} />
      )}

      <div className="portfolio-subnav__more-actions">
        {showLimitFilter && (
          <NumberOfResultsPerPageFilter
            limit={limit}
            updateLimit={updateLimit}
          />
        )}

        {RenderLeadsToggle}

        {showSellerButtons && isAboveLaptop && (
          <>
            <ResolvePopup onHover preferUpwards popupContent={t('addLead')}>
              <Button
                disabled={isDisabled}
                icon={PlusIcon}
                onClick={onMakeNewLead}
              />
            </ResolvePopup>
            {isPrivate && (
              <ResolvePopup
                onHover
                preferUpwards
                popupContent={t('shareThisListing')}
              >
                <Button
                  icon={SendIcon}
                  disabled={isDisabled}
                  onClick={copyPrivateLink}
                />
              </ResolvePopup>
            )}
            {isPublic && (
              <>
                <Button
                  className={classNames('share-button', {
                    'new-feature':
                      !!currentUser &&
                      hasNotSeenDiscoveryFeature(
                        seenShareListingFeature,
                        FIFTH_RELEASE_DISCOVERING_FEATURE,
                      ),
                  })}
                  onClick={shareListingModal?.open}
                >
                  <ResolvePopup
                    onHover
                    preferUpwards
                    popupContent={t('shareThisListing')}
                  >
                    <p>
                      <SendIcon />
                    </p>
                  </ResolvePopup>
                </Button>

                <ShareListingModal
                  ref={shareListingModalRef}
                  url={getResolveUrl(businessLink)}
                  listingId={listingId}
                  listingName={listingTitle}
                  onChooseLeads={shareWithPreviousLeadsModal?.open}
                />
                <ShareListingWithPreviousLeads
                  ref={shareWithPreviousLeadsModalRef}
                  dealRoomId={dealRoom?._id}
                  dealRoomName={dealRoom?.name}
                  leadStagesCopies={dealRoom?.leadStagesCopies}
                  listingId={dealRoom?.listingId}
                  listingName={listingTitle}
                />
              </>
            )}
            <ResolvePopup onHover preferUpwards popupContent={t('dealNotes')}>
              <Button
                icon={NotesIcon}
                className="deal-notes-button"
                style={{ position: 'relative' }}
                onClick={onDealNote}
              >
                <Counter
                  count={
                    dealRoom?.unreadNotesCount || dealRoom?.unrepliedNotesCount
                  }
                  isPositionAbsolute
                  size={SIZE.SMALL}
                  variant={
                    dealRoom?.unreadNotesCount
                      ? COUNTER_VARIANTS.ORANGE
                      : COUNTER_VARIANTS.SILVER
                  }
                >
                  {dealRoom?.unreadNotesCount || dealRoom?.unrepliedNotesCount}
                </Counter>
              </Button>
            </ResolvePopup>

            {(canSendBroadcastMessage || canSendBroadcastPulseCheck) && (
              <ResolvePopup
                onHover
                preferUpwards
                popupContent={t(
                  canSendBroadcastMessage
                    ? 'broadcastMessage'
                    : 'broadcastPulseCheck',
                )}
              >
                <Button icon={SpeakerphoneIcon} onClick={openBroadcastModal} />
              </ResolvePopup>
            )}
          </>
        )}

        {getHasElementsInArray(dropdownOptions) && (
          <MoreDropdown options={dropdownOptions} />
        )}
      </div>

      {NewLeadModal}
      {InviteViewerModal}
      {RenderedBroadcastModal}

      <DealNotes
        dealRoomId={dealRoom?._id}
        dealRoomName={dealRoom?.name}
        ref={dealNotesDrawerRef}
        updateDealRoom={updateDealRoom}
      />
    </div>
  );
};

export default PortfolioSubnav;
